import React from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { FileText } from "lucide-react";
import HashTag from "../components/HashTag";
import {
  Story,
  StoryHero,
  StoryNav,
  StoryOutro,
  Section,
  Figure,
  Stat,
  StatRow,
  Card,
  Eq,
  Prose,
  Split,
  Bullets,
  Steps,
  fade,
} from "../components/story";

const IMG = "/images/projects/underwater-swarms";

const PAPER =
  "https://www.sciencedirect.com/science/article/abs/pii/S1389128620300165";
const WCNC = "https://ieeexplore.ieee.org/abstract/document/8885489";

const TAGS = [
  "UnderwaterComms",
  "DistributedMIMO",
  "Acoustics",
  "MagneticInduction",
  "Swarm",
  "Beamforming",
  "TimeSync",
];

const NAV = [
  ["problem", "The problem"],
  ["why", "Why sync is hard"],
  ["idea", "The idea"],
  ["protocol", "Protocol"],
  ["hardware", "Testbed"],
  ["results", "Results"],
  ["limits", "Limits"],
  ["next", "Next steps"],
];

export default function UnderwaterMimoStory() {
  return (
    <Story>
      <Helmet>
        <title>Hybrid Acoustic &amp; Magnetic-Induction Distributed MIMO | Soham Desai</title>
        <meta
          name="description"
          content="Underwater robot swarms cannot synchronise over sound, it is 200,000× too slow. Metamaterial-enhanced magnetic induction carries the clock, acoustics carries the data. A USRP N210 testbed showed 4–8 dB SNR gain over pure acoustic sync."
        />
        <meta
          property="og:title"
          content="Hybrid Acoustic & Magnetic-Induction Distributed MIMO for Robot Swarms"
        />
        <meta
          property="og:description"
          content="M²I for near-instant synchronisation, acoustics for long-range data. A hardware-validated distributed MIMO system for underwater swarms."
        />
        <meta property="og:image" content={`${IMG}/lab-testbed.jpg`} />
      </Helmet>

      <StoryHero
        image={`${IMG}/lab-testbed.jpg`}
        breadcrumb={{ to: "/projects/underwater-comms", label: "Underwater Comms" }}
        kicker="Underwater distributed MIMO · University at Buffalo"
        title={
          <>
            Hybrid Acoustic &amp;{" "}
            <span className="text-accent">Magnetic-Induction</span> Distributed MIMO
          </>
        }
        lead={
          <>
            Distributed MIMO needs every transmitter to agree on time. Underwater the only
            long-range carrier is sound, and at{" "}
            <strong className="text-foreground">1500 m/s</strong> a synchronisation packet is
            milliseconds stale by the time it arrives. We gave the swarm a second, far faster
            channel dedicated to timing, and let acoustics do what it is actually good at.
          </>
        }
        stats={[
          { value: "4–8", unit: "dB", label: "SNR gain over pure acoustic synchronisation" },
          { value: "2×1", label: "distributed MIMO on real USRP N210 hardware" },
          { value: "M²I", label: "metamaterial-enhanced magnetic induction sync", amber: true },
        ]}
        chips={[
          "SDAMM testbed · USRP N210 + GNU Radio",
          "Computer Networks, 2020",
          "IEEE WCNC, 2019",
        ]}
      />

      <StoryNav items={NAV} />

      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fade}
          className="flex flex-wrap gap-x-5 gap-y-2 pt-8"
        >
          <a
            href={PAPER}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-accent text-sm hover:text-accent-hover transition-colors"
          >
            <FileText className="w-4 h-4" /> Computer Networks (2020)
          </a>
          <a
            href={WCNC}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-accent text-sm hover:text-accent-hover transition-colors"
          >
            <FileText className="w-4 h-4" /> IEEE WCNC (2019)
          </a>
        </motion.div>

        {/* ══════════ 1 · PROBLEM ══════════ */}
        <Section
          id="problem"
          kicker="Why · the problem"
          title="A swarm that cannot talk fast enough to cooperate"
          lead="Autonomous underwater vehicles are only useful in numbers, mapping, survey, and inspection all want many cheap robots rather than one expensive one. But every one of those robots is stuck behind the same bottleneck: the acoustic channel is narrow, slow, and violently multipath, and a single node simply cannot push enough data back to the surface."
        >
          <StatRow cols={3}>
            <Stat value="1500" unit="m/s" label="nominal speed of sound in seawater" />
            <Stat
              value="10–100"
              unit="kHz"
              label="practical acoustic band; usable bandwidth shrinks as range grows"
              amber
            />
            <Stat value="66.7" unit="ms" label="one-way delay across a 100 m acoustic hop" />
          </StatRow>

          <Prose>
            <p>
              Acoustic bandwidth is not a fixed number, it is a function of range. Absorption in
              seawater rises steeply with frequency, so a link that can use tens of kilohertz over
              a hundred metres is pushed down to a few kilohertz over tens of kilometres. Whatever
              band you land in, it is orders of magnitude narrower than anything terrestrial radio
              would offer.
            </p>
            <p>
              Distributed MIMO is the textbook answer. If several vehicles transmit the same data
              as one virtual antenna array, their signals can be made to add constructively at the
              surface receiver: more range, more reliability, more throughput, without a bigger
              transducer on any single robot.
            </p>
            <p>
              The catch is that coherent combining is a{" "}
              <strong className="text-foreground">phase</strong> operation. Every cooperating
              transmitter has to agree on a common time and frequency reference to a small fraction
              of a symbol period. Above water that is what GPS or a radio sync beacon is for.
              Underwater there is neither.
            </p>
          </Prose>
        </Section>

        {/* ══════════ 2 · WHY SYNC IS HARD ══════════ */}
        <Section
          id="why"
          kicker="The core difficulty"
          title="Why you cannot synchronise a swarm over sound"
          lead="Synchronising over the same acoustic channel you are trying to beamform on is circular, and it does not converge."
        >
          <Split>
            <Card title="Sound is too slow to carry a clock">
              <p>
                At 1500 m/s a 100 m hop costs 66.7 ms of propagation, and even 5 m between
                neighbours costs 3.3 ms. By the time a timing packet arrives the nodes have moved,
                the geometry has changed, and the correction is describing the past. That delay
                also has to be estimated and removed, which means knowing the inter-node range,
                which is itself what you were trying to measure.
              </p>
            </Card>
            <Card title="And the channel corrupts the estimate">
              <p>
                Acoustic multipath spreads the arrival over tens of milliseconds, so even the
                <em> measurement</em> of arrival time is ambiguous. Vehicle motion adds a Doppler
                scaling that stretches the waveform itself, biasing the frequency estimate the
                beamformer depends on.
              </p>
            </Card>
          </Split>

          <Card accent title="The insight">
            <p>
              Timing and data have completely different requirements. Data needs{" "}
              <strong className="text-foreground">range</strong>; timing needs{" "}
              <strong className="text-foreground">immediacy</strong>, and only over the short
              distances that separate neighbouring swarm members. There is no reason both have to
              travel on the same carrier.
            </p>
          </Card>
        </Section>

        {/* ══════════ 3 · THE IDEA ══════════ */}
        <Section
          id="idea"
          kicker="The solution"
          title="Two channels, each doing what it is good at"
          lead="Magnetic induction couples through water as a quasi-static near field rather than a propagating wave. It is short-range and it does not scale gracefully, but over the few metres separating neighbouring vehicles its transit is tens of microseconds instead of milliseconds, and there is no propagating wave to reflect, so no multipath ambiguity in the arrival estimate. That makes it a poor data link and an excellent clock."
        >
          <Steps
            items={[
              [
                "M²I",
                "The timing plane",
                "Metamaterial-enhanced magnetic induction carries synchronisation between neighbouring nodes with negligible delay and no multipath ambiguity.",
              ],
              [
                "AC",
                "The data plane",
                "Acoustic transmission carries the actual payload to the distant surface base station, beamformed across the whole swarm.",
              ],
            ]}
          />

          <StatRow cols={3}>
            <Stat value="3.3" unit="ms" label="acoustic transit across a 5 m node spacing" amber />
            <Stat value="~30" unit="µs" label="magnetic-induction transit over the same 5 m" />
            <Stat value="1/r³" label="MI field decay, the reason range is the hard part" />
          </StatRow>

          <Prose>
            <p>
              That 1/r³ field decay (1/r⁶ in received power) is why magnetic induction is usually
              dismissed for anything past a metre or two. The{" "}
              <strong className="text-foreground">metamaterial shell</strong> is what makes it
              practical: a spherical arrangement of resonant unit cells around the coil, engineered
              to present an effectively negative permeability. Following the M²I literature, the
              shell acts as a magnetic lens that amplifies the field around the transmitter,
              extending the usable coupling range well beyond a bare coil, enough to cover a swarm
              formation rather than a workbench.
            </p>
            <p className="text-sm text-muted-foreground">
              It buys range; it does not change the exponent. MI remains a near-field technique,
              which is exactly why it is used for timing between neighbours and never for the data
              path to the surface.
            </p>
          </Prose>

          <Eq>
            achievable rate ≈ B<sub>acoustic</sub> · log₂(1 + SNR) &nbsp;·&nbsp; coherent combining
            requires Δt ≪ symbol period
          </Eq>
        </Section>

        {/* ══════════ 4 · PROTOCOL ══════════ */}
        <Section
          id="protocol"
          kicker="How it works"
          title="The three-phase cooperative protocol"
          lead="A master node coordinates the swarm. Each transmission cycle runs the same three phases, and each phase uses the channel that suits it."
        >
          <Figure
            src={`${IMG}/system-diagram.png`}
            alt="Three-phase protocol: beacon broadcast, master-slave M2I synchronisation, and beamformed acoustic transmission"
            caption="(a) The surface base station broadcasts an acoustic beacon that every node uses for channel estimation. (b) The master node synchronises its slaves over the M²I link, the metamaterial shell around the MI transmitter extending the usable range. (c) The now-coherent swarm beamforms its acoustic signals back to the base station."
          />

          <Steps
            items={[
              [
                "1",
                "Beacon & channel estimation",
                "The base station broadcasts an acoustic beacon. Every node estimates its own channel to the surface from that single broadcast, so no per-node feedback is needed.",
              ],
              [
                "2",
                "Master–slave M²I sync",
                "The master sends a timing packet over magnetic induction. Each slave detects the packet, estimates frequency offset, and computes Δt against its own clock, returning a timestamp.",
              ],
              [
                "3",
                "Beamformed acoustic transmit",
                "With a shared reference, the nodes apply their beamforming codebook weights and transmit together, so the signals combine coherently at the base station.",
              ],
              [
                "4",
                "Detect & demodulate",
                "At the surface: packet detection, synchronisation, band-pass filtering, channel estimation, then demodulation of the combined waveform.",
              ],
            ]}
          />

          <Figure
            src={`${IMG}/fig-3.png`}
            alt="Signal-processing block diagram of the master node, slave node and base station chains"
            caption="The full transceiver chain. Each node carries two front ends: an acoustic Tx/Rx for data and beamforming, and an M²I Tx/Rx for timing. On the slave, the M²I path runs packet detection → frequency offset → time estimation for sync, feeding Δt back into the acoustic transmit timing. The base station side is a conventional detect / sync / channel-estimate / demodulate chain, with Alamouti coding across the distributed transmitters."
          />
        </Section>

        {/* ══════════ 5 · HARDWARE ══════════ */}
        <Section
          id="hardware"
          kicker="The testbed · SDAMM"
          title="Built on a blank bench, not in simulation"
          lead="The whole point was to show this outside of MATLAB. The SDAMM testbed is a two-transmitter, one-receiver link built from real parts: USRP N210 radios running GNU Radio for the M²I plane, custom acoustic transceiver boards with power amplifiers and a preamplifier for the acoustic plane, and hydrophones suspended in a water tank."
        >
          <Split>
            <Figure
              src={`${IMG}/lab-testbed.jpg`}
              alt="USRP N210 software-defined radios driving 3D-printed metamaterial shells and MI antennas"
              caption="The M²I plane. Two USRP N210s drive the magnetic-induction antennas (the small green coils); the blue 3-D-printed spheres are the metamaterial shells, each face carrying a resonant unit cell. The right-hand shell is shown opened to expose the coil inside."
            />
            <Figure
              src={`${IMG}/fig-2.png`}
              alt="Acoustic transceiver bench with TX-A, TX-B and RX boards, power amplifiers and preamplifier"
              caption="The acoustic plane. Two transmit boards (TX-A, TX-B) with their power amplifiers, a receive board (RX) behind a Teledyne VP2000 preamplifier, and the bench supplies that feed them. This is the hardware that has to stay phase-coherent."
            />
          </Split>

          <Figure
            src={`${IMG}/fig-5.jpg`}
            alt="Two transmit hydrophones and one receive hydrophone suspended in a water tank"
            caption="The wet end: TX-A and TX-B transmitting, RX receiving, in the lab water tank. A confined tank is a deliberately hostile multipath environment, every wall is a reflector, which makes it a fair stress test for a synchronisation scheme."
          />

          <Card title="What actually ran on it">
            <Bullets
              items={[
                "Distributed beamforming and Alamouti space-time coding across the two transmit nodes.",
                "M²I synchronisation packets with frequency-offset estimation and timestamp return.",
                "SNR, BER and throughput measured against a pure-acoustic-sync baseline on the same link and the same hardware.",
              ]}
            />
            <p className="pt-2 text-sm text-muted-foreground">
              Alongside it, on the same bench: an end-to-end OFDM transceiver (FFT/IFFT, 4-QAM)
              implemented in Verilog on a DE2i-150 FPGA, a separate build, not part of the SDAMM
              measurements above.
            </p>
          </Card>
        </Section>

        {/* ══════════ 6 · RESULTS ══════════ */}
        <Section
          id="results"
          kicker="Results"
          title="4–8 dB, measured, against the honest baseline"
          lead="The comparison that matters is not hybrid-versus-nothing, it is hybrid-versus-the-thing-people-actually-do: synchronising the same swarm over the acoustic channel itself. On identical hardware and identical links, the M²I-synchronised system delivered 4–8 dB more SNR at the receiver."
        >
          <StatRow cols={3}>
            <Stat value="4–8" unit="dB" label="SNR improvement over acoustic-only sync" />
            <Stat value="↓" label="bit-error rate at matched transmit power" amber />
            <Stat value="↑" label="throughput on the same acoustic bandwidth" />
          </StatRow>

          <Prose>
            <p>
              That gain is the direct cash value of a better clock. Residual timing error shows up
              as phase error at the receiver, and phase error is exactly what destroys coherent
              combining, the array stops adding and starts partially cancelling. Removing the
              propagation delay from the sync path removes most of that residual.
            </p>
            <p>
              The work was published in{" "}
              <a
                href={PAPER}
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent hover:text-accent-hover underline underline-offset-4"
              >
                Computer Networks
              </a>{" "}
              (2020) and, in its earlier form, at{" "}
              <a
                href={WCNC}
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent hover:text-accent-hover underline underline-offset-4"
              >
                IEEE WCNC
              </a>{" "}
              (2019). It also earned a Dean&rsquo;s Award at the University at Buffalo.
            </p>
          </Prose>
        </Section>

        {/* ══════════ 7 · LIMITS ══════════ */}
        <Section
          id="limits"
          kicker="Honesty · what this does not claim"
          title="Where the hybrid idea runs out"
          lead="The synchronisation plane inherits magnetic induction's fundamental limits. Nothing about the metamaterial shell repeals physics; it buys headroom, not immunity."
        >
          <Card>
            <Bullets
              items={[
                "M²I range is short. It synchronises neighbours in a formation, not vehicles spread across a survey area, so the swarm has to hold a loose formation for the scheme to apply.",
                "The metamaterial shell is a resonant structure, so it is narrowband and it has to be tuned. Detuning from temperature, pressure or fabrication tolerance costs range.",
                "Results are from a lab water tank. Tank multipath is severe but the geometry is static and the range is short, an open-water deployment adds motion, current, and real Doppler scaling.",
                "The testbed is 2×1. The synchronisation argument scales in principle, but N-node coherence was not demonstrated on hardware.",
              ]}
            />
          </Card>
        </Section>

        {/* ══════════ 8 · NEXT ══════════ */}
        <Section
          id="next"
          kicker="Next steps"
          title="Where this goes next"
          lead="Every open item is about moving from a validated principle to a fieldable system."
        >
          <motion.div variants={fade} className="grid gap-4 sm:grid-cols-2">
            {[
              [
                "Open-water trial",
                "Take the same two-node link out of the tank and into moving water, where relative motion produces genuine Doppler scaling rather than a static multipath pattern.",
              ],
              [
                "Scaling past two transmitters",
                "Demonstrate coherent combining across N nodes, where sync error accumulates through the master–slave topology rather than over a single hop.",
              ],
              [
                "Wideband / tunable metamaterial",
                "A shell that can retune in situ would tolerate the thermal and pressure drift a deployed vehicle actually sees.",
              ],
              [
                "Semantic payloads on the acoustic plane",
                "The follow-on work: if the link stays narrow, send meaning instead of bits, which is the thread picked up in the semantic video-transfer project.",
              ],
            ].map(([title, body]) => (
              <div
                key={title}
                className="rounded-lg border border-border bg-surface/50 p-5 hover:border-accent/30 transition-colors"
              >
                <h3 className="font-display text-base font-semibold text-foreground mb-2">
                  {title}
                </h3>
                <p className="text-sm text-muted leading-relaxed">{body}</p>
              </div>
            ))}
          </motion.div>
        </Section>

        <StoryOutro
          title="A slow channel does not have to mean a slow clock"
          tags={TAGS}
          TagComponent={HashTag}
          backTo="/projects/underwater-comms"
          backLabel="More from Underwater Comms"
        >
          <p className="mt-4 text-lg">
            The useful idea here is small and general: when one channel is bad at two different
            jobs, stop trying to make it better and split the jobs. Magnetic induction is a poor
            data link and a superb timing link; acoustics is the reverse. Running them together
            turned distributed MIMO underwater from a simulation result into a measured{" "}
            <strong className="text-foreground">4–8 dB</strong> on a bench I built myself.
          </p>
          <p>
            It is also where the rest of my work starts: the same instinct, separate what is
            observable from what is not, drives the GNSS and dead-reckoning projects.
          </p>
        </StoryOutro>
      </div>
    </Story>
  );
}
